<template>
    <div v-if="!disableinput" class="chat__post mb--xsmall">
        <div class="chat__profile-img">
            <img class="profil-image" :src="apiUrl + userProfileImage" />
        </div>

        <div class="chat__post__form--container" :class="{ 'collapsed': !showToolbar }" :style="containerStyle">

            <button @click="closeToolbar" class="chat__post__form--close-btn" aria-label='Luk "skriv indlæg" formular'>
                <span class="chat__post__form--close-btn-span-1"></span>
                <span class="chat__post__form--close-btn-span-2"></span>
            </button>

            <div class="chat__post__form">

                <!-- EDITOR -->
                <div class="quill-wrapper" :class="{ 'toolbar-visible': showToolbar }">
                    <QuillEditor :options="options" :theme="snow" contentType="html" v-model:content="text"
                        ref="quillEditor" @click="openTextEditor" @ready="onEditorReady" />
                </div>

                <!--
                  STATE CHIPS — summary of what is currently configured.
                  Placed BELOW the editor (after .quill-wrapper) so the collapsed
                  form still looks clean: the chips sit just above the action row.
                -->
                <div class="choices mb-2" v-if="hasState" :style="contentFadeStyle">
                    <div class="choices__inner">
                        <div class="choices__list choices__list--multiple" role="list">

                            <div class="choices__item" v-if="isGlobal && !hideWriteToAll" role="listitem">
                                <span>Til: Hele kommunen</span>
                                <button type="button" class="choices__button" aria-label="Fjern hele kommunen"
                                    @click="isGlobal = false"></button>
                            </div>

                            <template v-if="!hideOtherRecipients">
                                <div class="choices__item" v-for="tag in selectedTags" :key="`chip-${tag.id}`"
                                    role="listitem">
                                    <span>Til: {{ tag.name }}</span>
                                    <button type="button" class="choices__button" :aria-label="`Fjern ${tag.name}`"
                                        @click="removeTag(tag)"></button>
                                </div>

                                <div class="choices__item"
                                    v-if="includeSubDepartments && selectedTags.length > 0 && !isGlobal"
                                    role="listitem">
                                    <span>Nedarv til underafdelinger</span>
                                    <button type="button" class="choices__button"
                                        aria-label="Fjern medtagelse af underafdelinger"
                                        @click="includeSubDepartments = false"></button>
                                </div>
                            </template>

                            <div class="choices__item" v-if="selectedImpersonator" role="listitem">
                                <span>Som: {{ selectedImpersonator.firstName }} {{ selectedImpersonator.lastName
                                    }}</span>
                                <button type="button" class="choices__button" aria-label="Annuller afsender"
                                    @click="selectedImpersonator = null"></button>
                            </div>

                            <div class="choices__item" v-for="(f, idx) in files" :key="'file-' + idx" role="listitem">
                                <span>Fil: {{ f.name }}</span>
                                <button type="button" class="choices__button" aria-label="Fjern fil"
                                    @click="removeFile(idx)"></button>
                            </div>

                            <div class="choices__item" v-if="videoFeed" role="listitem">
                                <span>Video: {{ videoFeedLabel }}</span>
                                <button type="button" class="choices__button" aria-label="Fjern video"
                                    @click="clearVideo"></button>
                            </div>

                        </div>
                    </div>
                </div>

                <!--
                  ACTION ROW — recipients / send-as / media (file + video) popovers.
                  Triggers size to their content; the send button stays absolute bottom-right.
                -->
                <div class="chat-dropdowns__container" :style="{ 'padding-right': '55px', ...contentFadeStyle }">

                    <!--
                      Recipients (audience). The trigger only shows when at least
                      one of the two sub-sections (toggle / tag picker) is visible;
                      if both are hidden by admin flags the trigger disappears too
                      so there's nothing dangling that opens an empty popover.
                    -->
                    <div class="chat-dropdown" v-if="!isGroup && (!hideWriteToAll || !hideOtherRecipients)">
                        <button type="button" class="btn btn--sm"
                            :class="{ show: openMenuName === 'recipients' }" @click="toggleMenu('recipients')"
                            aria-haspopup="true" :aria-expanded="openMenuName === 'recipients'">
                            <div class="btn__component__text">Modtagere</div>
                            <!-- <div class="btn__component__icon icon-users3"></div> -->
                        </button>
                        <div class="chat-dropdown-menu" ref="recipientsMenu"
                            :class="{ show: openMenuName === 'recipients' }" style="width: 300px;">

                            <div class="form-group" v-if="!hideWriteToAll">
                                <label class="form-group__label" for="label1">Vælg modtager</label>
                                <div class="chat__checkboxes" id="label1">
                                    <div class="form-check">
                                        <label class="checkbox">
                                            Til hele kommunen
                                            <input v-model="isGlobal" name="isGlobal" class="interesttag"
                                                type="checkbox" />
                                            <div class="checkbox__indicator"></div>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div class="form-group" v-if="!hideOtherRecipients">
                                <form id="skillsForm" @submit.prevent>
                                    <label class="form-group__label" for="areas">Andre modtagere</label>
                                    <div class="choices" ref="choicesContainer" data-type="select-multiple"
                                        role="combobox" aria-autocomplete="list" aria-haspopup="true"
                                        :aria-expanded="filteredTags.length > 0">
                                        <div class="choices__inner">
                                            <input v-model="areaquery" @focusout="handleChoicesBlur" @input="filterTags"
                                                type="search" class="choices__input choices__input--cloned"
                                                autocomplete="off" autocapitalize="off" spellcheck="false"
                                                aria-autocomplete="list" aria-label="Find afdeling"
                                                placeholder="Find afdeling" style="min-width: 17ch; width: 1ch;">
                                            <div class="choices__list choices__list--multiple" role="listbox">
                                                <div class="choices__item" v-for="tag in selectedTags" :key="tag.id"
                                                    role="option" aria-selected="true">
                                                    <span>{{ tag.name }}</span>
                                                    <button type="button" class="choices__button"
                                                        :aria-label="`Fjern ${tag.name}`"
                                                        @click="removeTag(tag)"></button>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="choices__list choices__list--dropdown"
                                            :class="{ 'is-active': filteredTags.length > 0 }">
                                            <ul class="choices__list">
                                                <li @click="selectTag(tag)" v-for="tag in filteredTags" :key="tag.id"
                                                    role="option">
                                                    <button class="dropdown-option" type="button">{{ tag.name
                                                    }}</button>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>

                                    <div class="chat__checkboxes" v-if="selectedTags.length > 0 && !isGlobal">
                                        <div class="form-check">
                                            <label class="checkbox">
                                                Nedarv til underafdelinger
                                                <input v-model="includeSubDepartments" name="includeSubDepartments"
                                                    class="interesttag" type="checkbox" />
                                                <div class="checkbox__indicator"></div>
                                            </label>
                                        </div>
                                    </div>
                                </form>
                            </div>

                        </div>
                    </div>

                    <!-- Send-as (impersonator) -->
                    <div class="chat-dropdown" v-if="allowedImpersonators.length">
                        <button type="button" class="btn btn--sm"
                            :class="{ show: openMenuName === 'sendas' }" @click="toggleMenu('sendas')"
                            aria-haspopup="true" :aria-expanded="openMenuName === 'sendas'">
                            <div class="btn__component__text">{{ selectedImpersonator ? selectedImpersonator.firstName :
                                'Send som'
                            }}</div>
                            <!-- <div class="btn__component__icon icon-user"></div> -->
                        </button>
                        <div class="chat-dropdown-menu" ref="sendasMenu" :class="{ show: openMenuName === 'sendas' }"
                            style="width: 260px;">
                            <div class="form-group">
                                <label class="form-group__label" for="label2">Send på vegne af</label>
                                <div class="select">
                                    <select title="Vælg Afsender" v-model="selectedImpersonator">
                                        <option :value="null">Dig selv</option>
                                        <option :value="user" v-for="user in allowedImpersonators"
                                            :key="user.userAlias">
                                            {{ user.firstName }} {{ user.lastName }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!--
                      MEDIE — single dropdown combining file upload + video links.
                      Only renders if at least one of the attachment types is available.
                    -->
                    <div class="chat-dropdown" v-if="allowFileUpload || videoFeeds.length > 0">
                        <button type="button" class="btn btn--sm"
                            :class="{ show: openMenuName === 'media' }" @click="toggleMenu('media')"
                            aria-haspopup="true" :aria-expanded="openMenuName === 'media'">
                            <div class="btn__component__text">Vedhæft</div>
                            <!-- <div class="btn__component__icon icon-paperclip"></div> -->
                        </button>
                        <div class="chat-dropdown-menu" ref="mediaMenu" :class="{ show: openMenuName === 'media' }"
                            style="width: 300px;">

                            <!-- File upload -->
                            <div class="form-group" v-if="allowFileUpload && !isUploadingFile">
                                <div class="form-file">
                                    <label for="fileupload">Vælg fil</label>
                                    <input id="fileupload" type="file" name="fileUpload" multiple @change="handleFileUpload" />
                                </div>
                                <!-- Inline validation error (replaces alert()) -->
                                <span v-if="fileError" role="alert"
                                    style="display:block; margin-top:6px; color: var(--color-danger, #c00); font-size: 0.875em;">
                                    {{ fileError }}
                                </span>

                            </div>

                            <!--
                              Each allowed feed gets its own labelled input. The active
                              feed is set automatically the moment the user starts typing.
                            -->
                            <div class="form-group" v-if="videoFeeds.includes('dreamBrokerFeed')">
                                <label class="form-group__label" for="dreambrokerLink">Dreambroker link</label>
                                <input id="dreambrokerLink" type="text" class="form-control"
                                    :value="selectedVideoFeed === 'dreamBrokerFeed' ? videoFeed : ''"
                                    @input="setVideoUrl('dreamBrokerFeed', $event.target.value)"
                                    placeholder="Indsæt Dreambroker video link"
                                    aria-label="Indsæt Dreambroker video link" />
                            </div>

                            <div class="form-group" v-if="videoFeeds.includes('youtube')">
                                <label class="form-group__label" for="youtubeLink">YouTube link</label>
                                <input id="youtubeLink" type="text" class="form-control"
                                    :value="selectedVideoFeed === 'youtube' ? videoFeed : ''"
                                    @input="setVideoUrl('youtube', $event.target.value)"
                                    placeholder="Indsæt Youtube video link" aria-label="Indsæt Youtube video link" />
                            </div>
                        </div>
                    </div>

                </div>

                <!--
                  SEND BUTTON — kept at its existing absolute bottom-right slot.
                  Reflects an enabled/disabled state based on the text length.
                -->
                <button class="post-message chat__send" @click="addPost" :disabled="!canSend"
                    :style="{ opacity: canSend ? 1 : 0.45, cursor: canSend ? 'pointer' : 'not-allowed' }"
                    aria-label="Slå indlæg op">
                    <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1"
                        width="21" height="21" viewBox="0 0 350 350" xml:space="preserve">
                        <g id="icon"
                            style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: none; fill-rule: nonzero; opacity: 1;"
                            transform="translate(-1.9444444444444287 -1.9444444444444287) scale(3.89 3.89)">
                            <path
                                d="M 89.998 2.083 c 0.007 -0.167 -0.002 -0.333 -0.036 -0.497 c -0.005 -0.022 -0.013 -0.042 -0.019 -0.063 c -0.035 -0.143 -0.087 -0.282 -0.154 -0.416 c -0.019 -0.038 -0.038 -0.075 -0.059 -0.112 c -0.085 -0.147 -0.184 -0.289 -0.31 -0.415 c -0.125 -0.125 -0.266 -0.223 -0.412 -0.308 c -0.04 -0.023 -0.079 -0.043 -0.121 -0.063 c -0.13 -0.064 -0.264 -0.115 -0.402 -0.149 c -0.026 -0.006 -0.049 -0.017 -0.075 -0.022 c -0.162 -0.033 -0.326 -0.042 -0.491 -0.035 c -0.043 0.002 -0.084 0.008 -0.127 0.012 c -0.147 0.016 -0.292 0.046 -0.433 0.095 c -0.019 0.006 -0.038 0.005 -0.057 0.012 L 1.298 32.396 c -0.75 0.281 -1.259 0.982 -1.295 1.782 c -0.036 0.8 0.408 1.544 1.129 1.892 l 35.612 17.186 L 53.93 88.869 C 54.264 89.562 54.967 90 55.731 90 c 0.03 0 0.061 -0.001 0.091 -0.002 c 0.8 -0.036 1.5 -0.546 1.781 -1.295 L 89.859 2.748 c 0 -0.001 0.001 -0.002 0.001 -0.004 l 0.018 -0.048 c 0.006 -0.016 0.005 -0.032 0.011 -0.048 c 0.05 -0.145 0.081 -0.294 0.097 -0.445 C 89.99 2.163 89.996 2.124 89.998 2.083 z M 80.059 7.113 L 37.831 49.34 L 7.08 34.499 L 80.059 7.113 z M 55.501 82.92 L 40.66 52.168 L 82.888 9.94 L 55.501 82.92 z"
                                style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(0, 0, 0); fill-rule: nonzero; opacity: 1;"
                                transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round"></path>
                        </g>
                    </svg>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { QuillEditor, Quill } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import quillEmoji from 'quill-emoji'
import 'quill-emoji/dist/quill-emoji.css'

Quill.register('modules/emoji', quillEmoji)

export default {
    name: "ChatwallInputV2",
    components: { QuillEditor },
    props: {
        currentUserAlias: {
            type: String,
            required: true,
        },
        allowedImpersonators: {
            type: Array,
            required: true,
        },
        dropdownText: {
            type: String,
            default: "Indlæg muligheder"
        }
    },
    data() {
        return {
            isGlobal: false,
            isGroup: false,
            includeSubDepartments: false,
            groupId: null,
            // Default-on for the "Skriv til alle" toggle. When true, isGlobal
            // is preselected in mounted() so the post goes to the whole org by default.
            writeToAllDefault: false,
            // Hides the "Til hele kommunen" toggle in the recipients popover.
            // The underlying isGlobal value still drives behaviour, so combining
            // writeToAllDefault=true + hideWriteToAll=true means posts silently go to all.
            hideWriteToAll: false,
            // Hides the "Andre modtagere" tag picker in the recipients popover.
            hideOtherRecipients: false,
            isUploadingFile: false,
            allowFileUpload: false,
            allowImageUpload: false,
            allowPdfUpload: false,
            // Inline validation message for the file picker (replaces alert()).
            fileError: '',
            disableinput: false,
            userAlias: "",
            userName: "",
            userProfileImage: "",
            mainDepartment: [],
            placeholder: "",
            files: [],
            maxFiles: 10,
            maxFileSizeMb: 20,
            maxFileSizeBytes: 20 * 1024 * 1024,
            apiUrl: process.env.VUE_APP_API_URL,
            text: "",
            videoFeeds: [],
            selectedVideoFeed: "",
            videoFeed: "",
            editor: null,
            snow: "snow",
            // openMenuName replaces the original single `showPostMenu` flag.
            // Holds 'recipients' | 'sendas' | 'media' | null so only one
            // popover can be open at a time.
            openMenuName: null,
            selectedImpersonator: null,
            postExpireDays: 0,
            tags: [],
            selectedTags: [],
            filteredTags: [],
            choicesContainer: null,
            areaquery: "",
            showToolbar: false,
            // Flipped to true ~320ms after showToolbar becomes true — gates
            // overflow:visible on the container so popovers don't escape mid-expand.
            formExpanded: false,
            // Single timeout handle so toggling rapidly doesn't stack timers.
            expandTimer: null,
            quillInstance: null,
            localDropdownText: "",
            options: {
                modules: {
                    toolbar: [
                        [{ header: [2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        ['link'],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        ['emoji']
                    ],
                    'emoji-toolbar': true,
                    'emoji-textarea': false,
                    'emoji-shortname': true,
                },
                placeholder: document.querySelector(".chat-wrapper").dataset.placeholder,
                theme: 'snow'
            }
        };
    },
    emits: ["updatePost"],
    computed: {
        // Plain-text length of Quill output, used to enable/disable the send button.
        textLength() {
            if (!this.text) return 0;
            const tmp = document.createElement('div');
            tmp.innerHTML = this.text;
            return (tmp.textContent || tmp.innerText || '').trim().length;
        },
        canSend() {
            return this.textLength > 1;
        },
        // Drives whether the state-chip row should render at all.
        // Skips state that's been administratively hidden so we don't render
        // a chip the user can't interact with.
        hasState() {
            return (this.isGlobal && !this.hideWriteToAll)
                || (this.selectedTags.length > 0 && !this.hideOtherRecipients)
                || this.selectedImpersonator !== null
                || this.files.length > 0
                || (this.videoFeed && this.videoFeed.length > 0);
        },
        videoFeedLabel() {
            if (this.selectedVideoFeed === 'youtube') return 'YouTube';
            if (this.selectedVideoFeed === 'dreamBrokerFeed') return 'Dreambroker';
            return 'Tilføjet';
        },

        // Override the container's existing 200ms ease-in transition with a
        // longer cubic-bezier so the expand/collapse feels less choppy.
        containerStyle() {
            return {
                overflow: this.formExpanded ? 'visible' : 'hidden',
                transition: 'max-height 300ms cubic-bezier(0.4, 0, 0.2, 1)'
            };
        },
        // Fade-in for chips and the action row. On open, content fades in
        // 220ms after a 160ms delay (so it appears once the box is mostly
        // expanded). On close, it fades out synchronously with the collapse.
        contentFadeStyle() {
            return {
                opacity: this.showToolbar ? 1 : 0,
                transition: 'opacity 220ms ease-out',
                transitionDelay: this.showToolbar ? '160ms' : '0ms'
            };
        }
    },
    methods: {
        onEditorReady(editor) {
            this.quillInstance = editor;
        },
        toggleMenu(name) {
            this.openMenuName = this.openMenuName === name ? null : name;
        },
        closeAllMenus() {
            this.openMenuName = null;
            this.filteredTags = [];
            this.areaquery = "";
        },
        clearVideo() {
            this.videoFeed = "";
            if (this.videoFeeds.length > 1) {
                this.selectedVideoFeed = "";
            }
        },
        setVideoUrl(feedType, url) {
            this.selectedVideoFeed = feedType;
            this.videoFeed = url;
        },
        async addPost() {
            if (!this.canSend) return;

            var fileIds = [];
            if (this.files.length > 0) {
                fileIds = await this.uploadMediaFiles();
            }

            try {
                var tags = this.isGlobal ? "" : this.selectedTags.map(tag => tag.id).join(",");

                if (this.isGroup) {
                    tags = this.groupId;
                    this.isGlobal = false;
                }

                if (this.text.length <= 1) {
                    return;
                }

                const response = await fetch(
                    `${process.env.VUE_APP_API_URL}/api/MessageWall/AddNewPost`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Accept: "application/json",
                        },
                        body: JSON.stringify({
                            expireDays: Number(this.postExpireDays) || 0,
                            userAlias: this.selectedImpersonator != null ? this.selectedImpersonator.userAlias : this.currentUserAlias,
                            message: this.text,
                            isGlobal: this.isGlobal,
                            isGroupMessage: this.isGroup,
                            includeSubDepartments: !this.isGlobal && !this.isGroup && this.includeSubDepartments,
                            InterestAreas: tags,
                            fileIds: fileIds,
                            type: "post",
                            video: this.videoFeed != "" ? JSON.stringify({
                                type: this.selectedVideoFeed,
                                url: this.videoFeed,
                            }) : null,
                        }),
                    }
                );
                if (!response.ok) {
                    throw new Error("Fejl under oprettelse af kommentar");
                }

                // The endpoint sometimes returns 200 OK with an empty body
                // (or non-JSON content). Parse defensively and fall back to a
                // client-constructed post so the feed still updates correctly.
                let newPost = {};
                try {
                    const bodyText = await response.text();
                    if (bodyText && bodyText.trim().length > 0) {
                        newPost = JSON.parse(bodyText);
                    }
                } catch (parseErr) {
                    console.warn("AddNewPost response was not valid JSON:", parseErr);
                }

                newPost.date = new Date().toISOString();
                newPost.postType = newPost.postType || "post";
                newPost.isGroupMessage = false;
                if (!newPost.userAlias) {
                    newPost.userAlias = this.selectedImpersonator != null
                        ? this.selectedImpersonator.userAlias
                        : this.currentUserAlias;
                }
                if (!newPost.message) {
                    newPost.message = this.text;
                }

                this.$emit("update-post", newPost);
                this.reset();

            } catch (error) {
                console.error("Fejl under opdatering af comments:", error);
            }
        },
        filterTags() {
            const query = this.areaquery.toLowerCase();
            if (this.areaquery.length > 0) {
                this.filteredTags = this.tags.filter(tag =>
                    tag.name.toLowerCase().includes(query) &&
                    !this.selectedTags.some(t => t.id === tag.id)
                );
            } else {
                this.filteredTags = [];
            }
        },
        handleClickOutside(event) {
            if (this.openMenuName === null) return;

            const menus = [this.$refs.recipientsMenu, this.$refs.sendasMenu, this.$refs.mediaMenu].filter(Boolean);
            const clickedInsideAnyMenu = menus.some(m => m.contains(event.target));
            const clickedTrigger = event.target.closest('.chat-dropdown .btn');
            const clickedOnDropdownOption = event.target.closest('.dropdown-option');
            const clickedOnRemoveTag = event.target.classList.contains('choices__button');

            if (
                !clickedInsideAnyMenu
                && !clickedTrigger
                && !clickedOnDropdownOption
                && !clickedOnRemoveTag
            ) {
                this.closeAllMenus();
            } else if (clickedOnDropdownOption == null) {
                this.filteredTags = [];
                this.areaquery = "";
            }
        },
        handleChoicesBlur() {
            setTimeout(() => {
                const active = document.activeElement;
                const isInsideChoices = active && this.choicesContainer && this.choicesContainer.contains(active);
                const isInsideRecipients = this.$refs.recipientsMenu && this.$refs.recipientsMenu.contains(active);

                if (!isInsideChoices && !isInsideRecipients) {
                    this.filteredTags = [];
                    this.areaquery = "";
                }
            }, 10);
        },
        async getAreatags() {
            try {
                const response = await fetch(
                    `${process.env.VUE_APP_API_URL}/api/MessageWall/getareaTags`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Accept: "application/json",
                        },
                    }
                );
                if (!response.ok) {
                    throw new Error("Fejl under hentning af områder");
                }
                const tags = await response.json();
                this.tags = tags;

                this.selectedTags = this.tags.filter(tag => this.mainDepartment.includes(tag.id.toString()));
            } catch (error) {
                console.error("Der opstod en fejl:", error);
            }
        },
        async uploadMediaFiles() {
            this.isUploadingFile = true;
            try {
                const uploads = this.files.map(async (file) => {
                    const formData = new FormData();
                    formData.append("fileUpload", file);
                    const response = await fetch(
                        `${process.env.VUE_APP_API_URL}/api/MessageWall/UploadFile`,
                        {
                            method: "POST",
                            body: formData,
                        }
                    );
                    if (!response.ok) throw new Error("Upload fejlede!");
                    return await response.json();
                });
                return await Promise.all(uploads);
            }
            catch (error) {
                console.error(error);
                return [];
            }
            finally {
                this.isUploadingFile = false;
            }
        },
        openTextEditor() {
            if (this.showToolbar) return;
            this.showToolbar = true;
            // Wait until the container has finished its expand animation
            // before allowing overflow:visible (which lets popovers escape).
            if (this.expandTimer) clearTimeout(this.expandTimer);
            this.expandTimer = setTimeout(() => {
                if (this.showToolbar) this.formExpanded = true;
            }, 320);
        },
        closeToolbar() {
            // Lock down overflow first so any open popover is clipped during
            // the collapse animation rather than spilling outside the box.
            this.formExpanded = false;
            this.closeAllMenus();
            this.showToolbar = false;
            if (this.expandTimer) {
                clearTimeout(this.expandTimer);
                this.expandTimer = null;
            }
        },
        handleFileUpload(event) {
            const picked = Array.from(event.target.files || []);
            if (!picked.length) {
                this.fileError = '';
                return;
            }

            if (this.files.length + picked.length > this.maxFiles) {
                this.fileError = `Du kan højst vedhæfte ${this.maxFiles} filer.`;
                event.target.value = '';
                return;
            }

            // Reject the whole selection with an inline error message if any
            // picked file is too large or an explicitly disallowed type
            // (allowImageUpload=false → block images, allowPdfUpload=false →
            // block PDFs). Any other file is accepted.
            for (const file of picked) {
                const blockReason = this.fileBlockedReason(file);
                if (blockReason) {
                    this.fileError = blockReason;
                    event.target.value = '';
                    return;
                }
            }

            this.fileError = '';
            this.files.push(...picked);
            event.target.value = '';
        },
        removeFile(idx) {
            this.files.splice(idx, 1);
            this.fileError = '';
        },
        fileBlockedReason(file) {
            if (file.size > this.maxFileSizeBytes) {
                return `${file.name} er for stor (maks ${this.maxFileSizeMb} MB).`;
            }
            if (this.allowImageUpload === false && file.type.startsWith('image/')) {
                return 'Billeder er ikke tilladt.';
            }
            if (this.allowPdfUpload === false && file.type === 'application/pdf') {
                return 'PDF-filer er ikke tilladt.';
            }
            return null;
        },
        selectTag(tag) {
            this.selectedTags.push(tag);
            this.filterTags();
        },
        removeTag(tag) {
            this.selectedTags = this.selectedTags.filter(t => t.id !== tag.id);
            this.filterTags();
        },
        reset() {
            this.closeAllMenus();
            this.videoFeed = "";
            this.text = "";
            this.includeSubDepartments = false;
            this.files = [];
            this.fileError = '';
            const fileInput = document.getElementById('fileupload');
            if (fileInput) fileInput.value = '';
            this.$refs.quillEditor.setContents([]);
        },
    },
    mounted() {
        this.choicesContainer = this.$refs.choicesContainer;
        document.addEventListener('click', this.handleClickOutside);

        const chatwallWrapper = document.querySelector(".chat-wrapper");
        this.userAlias = chatwallWrapper.dataset.useralias;
        this.userProfileImage = chatwallWrapper.dataset.userprofileimage;
        this.allowFileUpload = chatwallWrapper.dataset.allowfileupload === 'true';
        this.allowImageUpload = chatwallWrapper.dataset.allowimageupload === 'true';
        this.allowPdfUpload = chatwallWrapper.dataset.allowpdfupload === 'true';

        // Recipient-control flags
        this.writeToAllDefault = chatwallWrapper.dataset.writetoalldefault === 'true';
        this.hideWriteToAll = chatwallWrapper.dataset.hidewritetoall === 'true';
        this.hideOtherRecipients = chatwallWrapper.dataset.hideotherrecipients === 'true';
        // Preselect "Til hele kommunen" when writeToAllDefault is on — applies whether
        // or not the toggle is also hidden (functionality must keep working).
        if (this.writeToAllDefault) {
            this.isGlobal = true;
        }
        this.mainDepartment = chatwallWrapper.dataset.maindepartment != undefined
            ? chatwallWrapper.dataset.maindepartment.split(',')
            : [];
        this.postExpireDays = chatwallWrapper.dataset.expire;
        this.disableinput = chatwallWrapper.dataset.disableinput === 'true';
        this.localDropdownText = chatwallWrapper.dataset.dropdowntext || this.dropdownText;

        var videoFeeds = chatwallWrapper.dataset.allowedvideofeed;
        if (videoFeeds) {
            this.videoFeeds = videoFeeds.split(',');
            if (this.videoFeeds.length == 1) {
                this.selectedVideoFeed = this.videoFeeds[0];
            }
        }

        this.isGroup = chatwallWrapper.dataset.isgroup === 'true';
        if (this.isGroup) {
            this.groupId = chatwallWrapper.dataset.groupid;
        }
        this.getAreatags();

        // Keep the original 'f' keydown shielding so the editor doesn't lose
        // keystrokes to global hotkey handlers.
        window.addEventListener('keydown', (e) => {
            if (e.isComposing) return;
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            if ((e.key || '').toLowerCase() !== 'f') return;

            const inQuill = document.activeElement &&
                document.activeElement.closest('.ql-editor');
            if (!inQuill) return;

            e.stopImmediatePropagation();
        }, true);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
    },
    setup: () => { },
};
</script>

<style>
/* The native file input renders its own button plus a trailing
   "No file chosen"/filename text. Since we always reset the input's value
   after each pick (to allow selecting more files across multiple rounds),
   that trailing text would only ever say "no file chosen" — even once
   files are attached and listed as chips above. Keep the native button
   visible, just hide its trailing text via a transparent input color, and
   restore an explicit color on the button itself so it stays legible. */
.form-file input[type="file"] {
    color: transparent;
}

.form-file input[type="file"]::file-selector-button,
.form-file input[type="file"]::-webkit-file-upload-button {
    color: initial;
}
</style>
